import React, { useState } from "react";
import {
  Box,
  VStack,
  Button,
  Flex,
  Text,
  RadioGroup,
  Icon,
  Heading,
  useColorModeValue,
  Input,
  useBreakpointValue,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { RiSparklingFill } from "react-icons/ri";
import { FaRedoAlt, FaCheck, FaTools, FaLightbulb } from "react-icons/fa";
import { TypewriterText } from "./TypewriterText";
import ContentBox from "./shared/ContentBox";

// Create motion components from Chakra UI components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

interface MarketplaceIdeasProps {
  marketplaceIdeas: string[];
  marketplaceDescription: string;
  visibleIdeas: number[];
  currentAnimatingIndex: number;
  onDescriptionChange: (value: string) => void;
  onCreateClick: (description: string) => void;
  onTryAgainClick: () => void;
  onCustomizeClick: () => void;
  onAnimationComplete: (index: number) => void;
  showCustomIdea: boolean;
  useCustomIdea: (customIdea: string) => void;
}

export const MarketplaceIdeas = ({
  marketplaceIdeas,
  marketplaceDescription,
  visibleIdeas,
  currentAnimatingIndex,
  onDescriptionChange,
  onCreateClick,
  onTryAgainClick,
  onAnimationComplete,
  onCustomizeClick,
  showCustomIdea,
  useCustomIdea,
}: MarketplaceIdeasProps) => {
  const selectedBgColor = useColorModeValue("#e6f7f0", "teal.800");
  const selectedBorderColor = useColorModeValue("#0a856a", "teal.400");
  const hoverBgColor = useColorModeValue("#f0fbf7", "teal.900");
  const unselectedBgColor = useColorModeValue("#f7f7f7", "gray.700");
  const unselectedBorderColor = useColorModeValue("gray.300", "gray.600");
  const [customIdea, setCustomIdea] = useState("");
  const customIdeaPlaceholder = useBreakpointValue({
    base: "Enter your business idea",
    xl: "In just a few words, describe your community and what they want to buy, sell, find, or transact",
  });

  const emptyIdeas = marketplaceIdeas.every((idea) => idea === "");

  // A create action is possible when either the user has selected one of the visible ideas
  // (which sets `marketplaceDescription`) *or* they have typed a custom idea.
  const canCreate = Boolean(marketplaceDescription) || (showCustomIdea && Boolean(customIdea));

  // Always use the currently selected idea. If a marketplaceDescription exists we forward it,
  // otherwise we assume the user wants to use their custom idea.
  const handleCreateClick = () => {
    if (marketplaceIdeas.includes(marketplaceDescription) && marketplaceDescription !== "") {
      onCreateClick(marketplaceDescription);
    } else {
      useCustomIdea(customIdea);
    }
  }

  return (
    <ContentBox>
      <VStack spacing={6} align="stretch" height="100%">
        <Heading
          fontSize={{ base: "xl", md: "1.4rem" }}
          textAlign="center"
          mb={2}
          pt={{ base: 1, md: 2 }}
          color="black"
        >
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Choose What Fits Your Community Best
          </MotionBox>
        </Heading>

        <Box pb={{ base: "100px", md: "90px" }}>
          <RadioGroup
            value={marketplaceDescription}
            onChange={onDescriptionChange}
          >
            <VStack spacing={6} align="stretch">
              <AnimatePresence>
                {marketplaceIdeas.map((idea, index) => (
                  (idea !== "") && (
                    <MotionFlex
                      key={idea}
                      direction="column"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{
                        opacity: visibleIdeas.includes(index) ? 1 : 0,
                        x: visibleIdeas.includes(index) ? 0 : -20,
                      }}
                      transition={{
                        duration: 0.7,
                        type: "spring",
                        stiffness: 100,
                      }}
                    >
                      <Box
                        as="button"
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        p={5}
                        borderRadius="xl"
                        borderWidth={2}
                        borderColor={
                          marketplaceDescription === idea
                            ? selectedBorderColor
                            : unselectedBorderColor
                        }
                        bg={
                          marketplaceDescription === idea
                            ? selectedBgColor
                            : unselectedBgColor
                        }
                        _hover={{
                          bg: hoverBgColor,
                          transform: "translateY(-2px)",
                          boxShadow: "md",
                          transition: "all 0.2s ease-in-out",
                        }}
                        onClick={() => onDescriptionChange(idea)}
                        visibility={
                          visibleIdeas.includes(index) ? "visible" : "hidden"
                        }
                        textAlign="left"
                        transition="all 0.2s ease-in-out"
                        boxShadow={
                          marketplaceDescription === idea ? "md" : "none"
                        }
                      >
                        <Flex flex="1" alignItems="center">
                          {visibleIdeas.includes(index) &&
                            index === currentAnimatingIndex && (
                              <TypewriterText
                                key={`${index}-${idea}`}
                                text={idea}
                                delay={0}
                                onComplete={() => {
                                  if (index < marketplaceIdeas.length - 1) {
                                    requestAnimationFrame(() => {
                                      onAnimationComplete(index);
                                    });
                                  }
                                }}
                              />
                            )}
                          {visibleIdeas.includes(index) &&
                            index < currentAnimatingIndex && (
                              <Text
                                fontSize="md"
                                color="black"
                                as="span"
                                textAlign="left"
                              >
                                {idea}
                              </Text>
                            )}
                          {index === marketplaceIdeas.length - 1 &&
                            currentAnimatingIndex > index &&
                            visibleIdeas.includes(index) && (
                              <Text
                                fontSize="md"
                                color="black"
                                as="span"
                                textAlign="left"
                              >
                                {idea}
                              </Text>
                            )}
                        </Flex>

                        {marketplaceDescription === idea && (
                          <MotionBox
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                              type: "spring",
                              stiffness: 260,
                              damping: 20,
                            }}
                          >
                            <Icon
                              as={FaCheck}
                              color={selectedBorderColor}
                              boxSize={5}
                              ml={3}
                            />
                          </MotionBox>
                        )}
                      </Box>
                    </MotionFlex>
                  )
                ))}
              </AnimatePresence>
              {showCustomIdea && (
                    <Box
                      as="button"
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                      px={2}
                      py={4}
                      borderRadius="xl"
                      borderWidth={2}
                      onClick={() => onDescriptionChange("")}
                      borderColor={
                        !marketplaceIdeas.includes(marketplaceDescription)
                        ? selectedBorderColor
                        : unselectedBorderColor}
                      bg={!marketplaceIdeas.includes(marketplaceDescription)
                        ? selectedBgColor
                        : unselectedBgColor}
                      _hover={{
                        bg: hoverBgColor,
                        transform: "translateY(-2px)",
                        boxShadow: "md",
                        transition: "all 0.2s ease-in-out",
                      }}
                    visibility={"visible"}
                    textAlign="left"
                    transition="all 0.2s ease-in-out"
                    boxShadow={!marketplaceIdeas.includes(marketplaceDescription)
                      ? "md" : "none"}
                  >
                    <Input
                      placeholder={customIdeaPlaceholder}
                      onChange={(e) => setCustomIdea(e.target.value)}
                      _placeholder={{ color: "gray.500" }}
                    />
                    {/* <Button
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      onClick={() => useCustomIdea(customIdea)}
                      maxW="fit-content"
                      minW="fit-content"
                      ml={2}
                      isDisabled={!customIdea}
                    >
                      Use This Idea
                      <Icon as={FaLightbulb} boxSize={3} />
                    </Button> */}
                  </Box>
              )}
            </VStack>
          </RadioGroup>
        </Box>
        <MotionBox
          position="absolute"
          bottom={8}
          left={0}
          right={0}
          px={8}
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity:
              emptyIdeas ||
              currentAnimatingIndex >= marketplaceIdeas.length - 1 ? 1 : 0,
            y:
              emptyIdeas ||
              currentAnimatingIndex >= marketplaceIdeas.length - 1 ? 0 : 20,
          }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <Flex justify="center" gap={4}>
            <Button
              display="inline-flex"
              alignItems="center"
              gap={2}
              px={5}
              maxW="fit-content"
              minW="fit-content"
              transition="all 0.2s ease"
              onClick={handleCreateClick}
              isDisabled={!canCreate}
            >
              Create <Icon as={RiSparklingFill} ml={1} />
            </Button>
            {!showCustomIdea && <Button
              display="inline-flex"
              alignItems="center"
              gap={2}
              px={5}
              maxW="fit-content"
              minW="fit-content"
              transition="all 0.2s ease"
              onClick={onCustomizeClick}
            >
              Customize <Icon as={FaTools} boxSize={3} ml={1} />
            </Button>}
          </Flex>
        </MotionBox>
      </VStack>
    </ContentBox>
  );
};
